import {
  PluginTypeEnumValues,
  type PluginTypeEnumTypes,
} from "@chat-ai/_database/enums";
import {
  BETA_FLAG,
  BETA_INCLUDED_FEATURES,
  PROFILE_CREATION_FLAG,
  type FeatureFlagType,
} from "@shared-utils/constants";

// Plugin metadata
export const pluginsMetadata = (
  pluginType: PluginTypeEnumTypes | undefined,
): {
  type: PluginTypeEnumTypes;
  name: string;
  description: string;
  examplePrompt: string;
  order: number;
  adminOnly: boolean;
  metricColor: string;
  hasConfig: boolean;
  requiredFeatureFlag?: FeatureFlagType;
} => {
  switch (pluginType) {
    case "WEBREADER":
      return {
        type: "WEBREADER",
        name: "Web",
        description: "Search the web for current information",
        examplePrompt: "Latest news about LEO Pharma",
        order: 1,
        adminOnly: false,
        metricColor: "var(--brand-tl-red-500)",
        hasConfig: false,
      };
    case "RESEARCH_AI":
      return {
        type: "RESEARCH_AI",
        requiredFeatureFlag: "RESEARCH_AI__APP__205402",
        name: "Deep Research",
        description:
          "Conduct comprehensive research tasks using advanced web search",
        examplePrompt: "Research the latest developments in mRNA vaccines",
        order: 2,
        adminOnly: false,
        metricColor: "var(--brand-ter-bluegray-500)",
        hasConfig: false,
      };
    case "CLINICAL_TRIALS_GOV":
      return {
        type: "CLINICAL_TRIALS_GOV",
        name: "ClinicalTrials.gov",
        description: "Search for clinical trials on ClinicalTrials.gov",
        examplePrompt: "Find latest clinical trials related to Adtralza",
        order: 3,
        adminOnly: false,
        metricColor: "var(--brand-ter-green-500)",
        hasConfig: false,
      };
    case "PUBMED":
      return {
        type: "PUBMED",
        name: "PubMed",
        description: "Search for articles on PubMed",
        examplePrompt: "Find articles related to Adtralza",
        order: 4,
        adminOnly: false,
        metricColor: "var(--brand-ter-blue-400)",
        hasConfig: false,
      };
    case "ISEARCH":
      return {
        type: "ISEARCH",
        name: "iSearch",
        description: "Search for information in iSearch",
        examplePrompt: "Find documents related to Adtralza",
        order: 5,
        adminOnly: false,
        metricColor: "var(--brand-ter-green-400)",
        hasConfig: true,
      };
    case "MEDDRA":
      return {
        type: "MEDDRA",
        name: "MedDRA",
        description:
          "Search and classify medical terms using MedDRA terminology",
        examplePrompt: "Classify the adverse event: patient experienced nausea",
        order: 6,
        adminOnly: false,
        metricColor: "var(--brand-ter-gray-500)",
        hasConfig: false,
      };
    case "HAS_DATABASE":
      return {
        type: "HAS_DATABASE",
        name: "HAS Database",
        description:
          "Search French drug evaluations (SMR/ASMR) from Haute Autorité de Santé",
        examplePrompt: "Find SMR rating for efavirenz",
        order: 7,
        adminOnly: false,
        metricColor: "var(--brand-ter-gray-400)",
        hasConfig: false,
      };

    // Admin only plugins
    case "SERVICE_NOW_KNOWLEDGE_ARTICLES":
      return {
        type: "SERVICE_NOW_KNOWLEDGE_ARTICLES",
        name: "Service Now",
        description: "Search knowledge articles in ServiceNow",
        examplePrompt: "How to reset my password?",
        order: 99,
        adminOnly: true,
        metricColor: "var(--brand-ter-gray-300)",
        hasConfig: false,
      };

    // Hidden plugins that are not shown to regular users
    case "IMAGE_GENERATION":
      return {
        type: "IMAGE_GENERATION",
        name: "Image Generator",
        description:
          "Generate or edit images from natural language instructions.",
        examplePrompt: "Generate an image of a cat",
        order: 1001,
        adminOnly: false,
        metricColor: "var(--brand-tl-yellow-500)",
        hasConfig: false,
      };
    case "CODE_INTERPRETER":
      return {
        type: "CODE_INTERPRETER",
        name: "Code Interpreter",
        description:
          "Run Python code to analyze data files, create charts, and generate spreadsheets or other files.",
        examplePrompt: "Create graph of normal distribution",
        order: 1002,
        adminOnly: false,
        metricColor: "var(--brand-tl-green-500)",
        hasConfig: false,
      };
    case "MS_OFFICE_WRITER":
      return {
        type: "MS_OFFICE_WRITER",
        name: "Document Generator",
        description:
          "Create downloadable Microsoft Word documents and PowerPoint presentations.",
        examplePrompt: "Create a document in Microsoft Word",
        order: 1003,
        adminOnly: false,
        metricColor: "var(--brand-ter-blue-500)",
        hasConfig: false,
      };
    case "TEXT_FILE_GENERATOR":
      return {
        type: "TEXT_FILE_GENERATOR",
        name: "Text File Generator",
        description:
          "Save complete text content as downloadable code, CSV, JSON, Markdown or HTML files.",
        examplePrompt: "Create a downloadable HTML dashboard",
        order: 1004,
        adminOnly: false,
        metricColor: "var(--extra-purple-500)",
        hasConfig: false,
      };
    case "ONEDRIVE":
      return {
        type: "ONEDRIVE",
        name: "OneDrive",
        description:
          "Browse and search your OneDrive files, and read text-based file content.",
        examplePrompt: "Search files in my OneDrive documents",
        order: 1005,
        adminOnly: false,
        metricColor: "var(--brand-ter-bluegray-400)",
        hasConfig: false,
      };
    case "CALENDAR":
      return {
        type: "CALENDAR",
        requiredFeatureFlag: "CALENDAR_PLUGIN__221745",
        name: "Calendar",
        description: "Access calendar and meeting transcripts",
        examplePrompt: "What meetings do I have today?",
        order: 1006,
        adminOnly: false,
        metricColor: "var(--brand-tl-blue-500)",
        hasConfig: false,
      };
    case "PROFILE_CREATION":
      return {
        type: "PROFILE_CREATION",
        requiredFeatureFlag: PROFILE_CREATION_FLAG,
        name: "Profile Creation",
        description: "Draft a reusable AI profile from this chat",
        examplePrompt: "Create an AI profile from this chat",
        order: 1007,
        adminOnly: false,
        metricColor: "var(--extra-cyan-500)",
        hasConfig: false,
      };

    default:
      console.error(`Unknown plugin type: ${pluginType}`);

      return {
        type: "IMAGE_GENERATION",
        name: "Unknown",
        description: "Unknown plugin type",
        examplePrompt: "",
        order: 99,
        adminOnly: false,
        metricColor: "",
        hasConfig: false,
      };
  }
};

/**
 * The plugins whose feature flag, if any, the given enabled-feature list
 * grants, with the Beta flag standing in for the Beta-included features as
 * `isFeatureEnabled` does on the server. The client's context meter uses it
 * to rebuild the capabilities prompt the route sends; the server resolves the
 * same gate through `getFeatureEnabledPlugins`, which also applies the
 * environment allowance and a cache.
 * @param enabledFeatures - The flags enabled for the viewer, as `allEnabledForMe` reports them
 */
export const featureEnabledPluginsFrom = (
  enabledFeatures: readonly FeatureFlagType[],
): PluginTypeEnumTypes[] =>
  PluginTypeEnumValues.filter((pluginType) => {
    const feature = pluginsMetadata(pluginType).requiredFeatureFlag;
    return (
      feature === undefined ||
      enabledFeatures.includes(feature) ||
      (BETA_INCLUDED_FEATURES.includes(feature) &&
        enabledFeatures.includes(BETA_FLAG))
    );
  });
